// @ts-nocheck
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {z} from "zod";
import {BaseButton, BaseCheckbox, BaseInput, toast} from "@/components";
import {dropStorage, getStorage, PERSISTENT_COOKIE_OPTIONS, setStorage} from "@/store/util.store";
import eyeActive from "../../assets/images/icon-eye-active.svg";
import eyeInactive from "../../assets/images/icon-eye-inactive.svg";
import useLogin from "../../hooks/useLogin";
import {useAuthStore} from "../../store/useAuth.store";
import {useLogo} from "../../util/logoUtils";

const mobileLoginContainerCls =
  "min-h-screen w-screen bg-[var(--background)] flex flex-col justify-center items-center p-0 m-0 fixed top-0 left-0 z-[var(--z-modal)] overflow-y-auto box-border portrait:h-[100svh] landscape:h-[100svh]";
const mobileLoginContentCls =
  "w-full max-w-full flex flex-col justify-center items-center min-h-screen p-5 box-border max-md:p-[var(--space-3-75)] max-[426px]:p-2.5 landscape:flex-row landscape:items-center landscape:justify-around landscape:p-5";
const logoContainerCls =
  "flex justify-center items-center mb-10 w-full max-[426px]:mb-[30px] landscape:mb-0 landscape:mr-10 landscape:shrink-0";
const logoCls = "w-[250px] h-auto max-w-[var(--size-75)] max-md:w-[220px] max-[426px]:w-[var(--size-50)]";
const formContainerCls =
  "w-full max-w-[var(--size-100)] flex justify-center items-center max-md:max-w-full landscape:flex-1 landscape:max-w-[var(--size-100)]";
const formCls = "w-full";
const formInnerCls = "w-full flex flex-col items-center";
const titleCls =
  "font-[var(--font-family)] text-2xl leading-[var(--leading-xl)] text-center font-extrabold mb-6 text-[var(--text-color)] max-md:text-2xl max-md:mb-[25px] max-[426px]:text-[var(--font-h2)] max-[426px]:mb-5";
const inputCls =
  "flex items-center justify-center w-full h-12 rounded-full bg-[var(--modal-background)] text-base text-[var(--placeholder)] pl-6 border border-[var(--custom-navbar-hover)] max-[426px]:h-12 max-[426px]:pl-5 [&_input::placeholder]:text-base [&_input::placeholder]:text-left [&_input::placeholder]:opacity-100 [&_input::placeholder]:font-medium [&_input::placeholder]:text-[var(--alternative-white)]";
const rememberMeContainerCls = "w-full flex justify-start items-center mb-[var(--space-3-75)] pl-1";
const rememberMeCheckboxCls =
  "font-[var(--font-family)] [&_.ant-checkbox]:rounded [&_.ant-checkbox-checked_.ant-checkbox-inner]:bg-[var(--button-background)] [&_.ant-checkbox-checked_.ant-checkbox-inner]:border-[var(--button-background)] hover:[&_.ant-checkbox-inner]:border-[var(--button-background)]";
const rememberMeTextCls = "text-[var(--text-color)] text-sm font-normal ml-2";
const forgotPasswordCls = "w-full text-center mb-[25px]";
const forgotPasswordLinkCls =
  "cursor-pointer font-[var(--font-family)] text-[var(--button-background)] text-sm transition-colors duration-200 hover:text-[var(--button-hover)]";
const errorMessageCls = "text-center mb-5 w-full";
const submitButtonCls =
  "font-[var(--font-family)] text-[var(--font-body-lg)] font-bold rounded-full w-full h-12 bg-[var(--button-background)] border-0 hover:bg-[var(--button-hover)]";
const noAccountTextCls = "cursor-default font-[var(--font-family)] text-[var(--text-color)]";
const loadingContainerCls =
  "min-h-screen w-screen bg-[var(--modal-background)] flex flex-col justify-center items-center fixed top-0 left-0 z-[var(--z-modal)]";
const loadingLogoCls = "w-[var(--size-30)] h-auto mb-[30px]";
const loadingSpinnerCls =
  "w-10 h-10 border-4 border-[var(--placeholder)] border-t-4 border-t-[var(--button-background)] rounded-full animate-spin";

const SPECIAL_CHAR_REGEX = /[´ñáéíóúáèìòùâêîôûäëïöüãõ¨';[(){}¡!"ª°·#$%^&*+=\\|~<>¿?]/;
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/;

const buildSchema = t =>
  z.object({
    username: z
      .string()
      .trim()
      .min(1, t("loginEmail"))
      .superRefine((email, ctx) => {
        if (SPECIAL_CHAR_REGEX.test(email)) {
          ctx.addIssue({code: "custom", message: t("registrationEmail_specialChar")});
          return;
        }
        if (!EMAIL_REGEX.test(email)) {
          ctx.addIssue({code: "custom", message: t("registrationEmail_message")});
          return;
        }
        const [localPart, domainPart] = email.split("@");
        if (
          localPart.startsWith(".") ||
          localPart.endsWith(".") ||
          localPart.startsWith("-") ||
          localPart.startsWith("+") ||
          localPart.endsWith("-") ||
          localPart.endsWith("+")
        ) {
          ctx.addIssue({code: "custom", message: t("registrationEmail_CharStartEnd")});
          return;
        }
        if (
          localPart.includes("..") ||
          localPart.includes("--") ||
          domainPart.includes("..") ||
          domainPart.includes("--")
        ) {
          ctx.addIssue({code: "custom", message: t("registrationEmail_CharConsecutive")});
          return;
        }
        for (const part of domainPart.split(".")) {
          if (part.startsWith("-") || part.endsWith("-")) {
            ctx.addIssue({code: "custom", message: t("registrationEmail_CharStartEnd")});
            return;
          }
        }
      }),
    password: z.string().min(1, t("loginPassword")),
  });

const MobileLogin = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const logo = useLogo();

  const [values, setValues] = useState({username: "", password: ""});
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const authStatus = useAuthStore(state => state.authStatus);
  const validateSession = useAuthStore(state => state.validateSession);
  const handleAuthForm = useAuthStore(state => state.handleAuthForm);

  const {postLogin, isPending, isError} = useLogin();

  useEffect(() => {
    validateSession();
    setStorage("isMobileLoginUser", "true");
  }, [validateSession]);

  useEffect(() => {
    const savedEmail = getStorage("rememberedEmail");
    const wasRemembered = getStorage("rememberMeChecked") === "true";
    if (savedEmail && wasRemembered) {
      setValues(v => ({...v, username: savedEmail}));
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    if (authStatus === "authenticated") {
      const user = useAuthStore.getState().user;
      const authorizedEmails = ["test01@googletest.com", "test01@gmailtest.com"];
      if (user && authorizedEmails.includes(user.email.toLowerCase())) {
        navigate("/lotoven/home", {replace: true});
      } else {
        navigate("/", {replace: true});
      }
    }
  }, [authStatus, navigate]);

  const handleChange = field => e => {
    setValues(v => ({...v, [field]: e.target.value}));
    if (errors[field]) setErrors(prev => ({...prev, [field]: undefined}));
  };

  const handleRememberMeChange = checked => {
    setRememberMe(checked);
    if (!checked) {
      dropStorage("rememberedEmail");
      dropStorage("rememberMeChecked");
    }
  };

  const persistRemember = trimmedUsername => {
    if (rememberMe) {
      setStorage("rememberedEmail", trimmedUsername, PERSISTENT_COOKIE_OPTIONS);
      setStorage("rememberMeChecked", "true", PERSISTENT_COOKIE_OPTIONS);
    } else {
      dropStorage("rememberedEmail");
      dropStorage("rememberMeChecked");
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    const result = buildSchema(t).safeParse(values);
    if (!result.success) {
      const nextErrors = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0];
        if (typeof key === "string" && !nextErrors[key]) nextErrors[key] = issue.message;
      }
      setErrors(nextErrors);
      return;
    }
    setErrors({});

    const trimmedUsername = result.data.username;
    if (trimmedUsername === "testaccount@gmail.com") {
      toast.error(t("loginUserNotFound"));
      return;
    }

    persistRemember(trimmedUsername);
    setStorage("isMobileLoginUser", "true");
    postLogin({username: trimmedUsername, password: result.data.password});
  };

  if (authStatus === "checking") {
    return (
      <div className={loadingContainerCls}>
        <img
          src={logo}
          alt="logo"
          className={loadingLogoCls}
        />
        <div className={loadingSpinnerCls}></div>
      </div>
    );
  }

  return (
    <div className={mobileLoginContainerCls}>
      <div className={mobileLoginContentCls}>
        <div className={logoContainerCls}>
          <img
            src={logo}
            alt="logo"
            className={logoCls}
          />
        </div>

        <div className={formContainerCls}>
          <form
            className={formCls}
            onSubmit={handleSubmit}
            autoComplete="off"
            noValidate
          >
            <div className={formInnerCls}>
              <h3 className={titleCls}>{t("loginSessionTitle")}</h3>

              <div style={{width: "100%", marginBottom: "var(--space-5)"}}>
                <BaseInput
                  placeholder={t("loginEmailPlaceholder")}
                  value={values.username}
                  onChange={handleChange("username")}
                  error={errors.username}
                  containerClassName={inputCls}
                />
              </div>

              <div style={{width: "100%", marginBottom: "var(--space-3)"}}>
                <BaseInput
                  type={showPassword ? "text" : "password"}
                  placeholder={t("loginPasswordPlaceholder")}
                  value={values.password}
                  onChange={handleChange("password")}
                  error={errors.password}
                  containerClassName={inputCls}
                  rightIcon={
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      aria-label="toggle password"
                      style={{background: "transparent", border: "none", padding: 0, cursor: "pointer"}}
                    >
                      <img
                        src={showPassword ? eyeActive : eyeInactive}
                        alt="toggle password"
                        style={{width: "var(--size-6)", height: "var(--size-6)"}}
                      />
                    </button>
                  }
                />
              </div>

              <div className={rememberMeContainerCls}>
                <BaseCheckbox
                  checked={rememberMe}
                  onChange={handleRememberMeChange}
                  className={rememberMeCheckboxCls}
                >
                  <span className={rememberMeTextCls}>{t("loginRememberEmail") || "Recordar mi correo"}</span>
                </BaseCheckbox>
              </div>

              <div className={forgotPasswordCls}>
                <button
                  type="button"
                  onClick={() => handleAuthForm("recover")}
                  className={forgotPasswordLinkCls}
                  style={{background: "transparent", border: "none", cursor: "pointer", padding: 0}}
                >
                  {t("loginForgotPassword")}
                </button>
              </div>

              {isError && (
                <div className={errorMessageCls}>
                  <span style={{color: "var(--color-danger-text)"}}>{t("loginIncorrectPasswordEmail")}</span>
                </div>
              )}

              <div style={{textAlign: "center", marginBottom: "var(--space-5)"}}>
                <BaseButton
                  type="submit"
                  variant="primary"
                  loading={isPending}
                  className={submitButtonCls}
                >
                  {t("loginSendBtn")}
                </BaseButton>
              </div>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "var(--space-3-5)",
                }}
              >
                <span className={noAccountTextCls}>
                  {t("loginNoAccount")}{" "}
                  <button
                    type="button"
                    onClick={() => navigate("/mobile-registration")}
                    className={
                      "font-[var(--font-family)] ml-[var(--space-1-5)] text-[var(--button-background)] no-underline cursor-pointer hover:text-[var(--button-hover)]"
                    }
                    style={{
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                      color: "var(--color-primary)",
                    }}
                  >
                    {t("loginRegister")}
                  </button>
                </span>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MobileLogin;
